import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomizationStore } from '../store/customizationStore';
import { useLocalizationStore } from '../store/localizationStore';
import { formatCurrency } from '../utils/units';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { OrderPDF } from './OrderPDF';
import { 
  Star, 
  Award, 
  Shield, 
  Clock, 
  MessageCircle, 
  X, 
  Quote, 
  MapPin, 
  ChevronLeft, 
  ChevronRight,
  Sparkles,
  Truck,
  Wrench,
  Heart,
  Download,
  Facebook,
  Instagram,
  Copy,
  Printer,
  Share2,
  Twitter,
  Linkedin,
  Mail,
  Phone,
  Calendar,
  Zap,
  Users,
  Timer,
  BadgeCheck,
  Check,
  ArrowRight,
  Globe,
  TrendingUp
} from 'lucide-react';

const VAT_RATE = 0.21; // 21% VAT

const shareOptions = [
  { icon: Facebook, label: 'Facebook', color: 'bg-[#1877F2]' },
  { icon: Instagram, label: 'Instagram', color: 'bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737]' },
  { icon: Twitter, label: 'Twitter', color: 'bg-[#1DA1F2]' },
  { icon: Linkedin, label: 'LinkedIn', color: 'bg-[#0A66C2]' }
];

// Live activity feed
const liveActivity = [
  {
    type: 'view',
    count: 24,
    location: 'New York, USA',
    product: 'Dining Tables',
    time: '2 minutes ago'
  },
  {
    type: 'customize',
    count: 12,
    location: 'Paris, France',
    product: 'Kitchen Surfaces',
    time: '5 minutes ago'
  },
  {
    type: 'quote',
    count: 8,
    location: 'Dubai, UAE',
    product: 'Custom Projects',
    time: '10 minutes ago'
  }
];

// Market stats
const marketStats = [
  {
    label: 'Average Order Value',
    value: '€6,250',
    trend: '+12%',
    icon: TrendingUp
  },
  {
    label: 'Active Customers',
    value: '2.4K',
    trend: '+8%',
    icon: Users
  },
  {
    label: 'Global Presence',
    value: '45+',
    trend: 'Countries',
    icon: Globe
  }
];

const containerVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 20,
      staggerChildren: 0.1
    }
  },
  exit: { opacity: 0, y: -50 }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export const Confirmation: React.FC = () => {
  const { 
    customizationOptions, 
    selectedMaterial, 
    customizationPath,
    selectedMaterials,
    formState 
  } = useCustomizationStore();
  const { currency, language } = useLocalizationStore();
  const [showCopied, setShowCopied] = useState(false);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const text = `Check out my custom ${customizationOptions?.type || 'material'} design!`;

    switch (platform) {
      case 'Facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'Twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'LinkedIn':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'Instagram':
        alert('To share on Instagram, please screenshot your design and share it through the Instagram app');
        break;
      default:
        break;
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  const handleEmailShare = () => {
    const subject = 'Check out my custom design';
    const body = 'I just designed this amazing piece using the configurator: ' + window.location.href;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="min-h-screen bg-[#1C1C1C] py-8 sm:py-16"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-16">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ 
              type: 'spring',
              stiffness: 200,
              damping: 20,
              delay: 0.2
            }}
            className="relative inline-block mb-6 sm:mb-8"
          >
            <div className="absolute inset-0 bg-gold/20 rounded-full blur-xl opacity-20 animate-pulse" />
            <Award className="w-16 h-16 sm:w-24 sm:h-24 text-gold relative" />
          </motion.div>
          
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-3xl sm:text-5xl font-bold text-white mb-4 sm:mb-6"
          >
            Thank You for Your Order!
          </motion.h2>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto mb-6 sm:mb-8"
          >
            {customizationOptions?.type === 'custom-project'
              ? "You've taken the first step towards creating your perfect space. Our premium design team will be in touch shortly."
              : 'Your design request has been received. Get ready to transform your space with our premium materials.'}
          </motion.p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8">
            <motion.div
              variants={itemVariants}
              className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6"
            >
              <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-gold mx-auto mb-3 sm:mb-4" />
              <h3 className="font-semibold text-white mb-1 sm:mb-2">Quick Response</h3>
              <p className="text-xs sm:text-sm text-white/70">Within 24 hours</p>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6"
            >
              <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-gold mx-auto mb-3 sm:mb-4" />
              <h3 className="font-semibold text-white mb-1 sm:mb-2">Quality Assured</h3>
              <p className="text-xs sm:text-sm text-white/70">10-year warranty</p>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6"
            >
              <Truck className="w-6 h-6 sm:w-8 sm:h-8 text-gold mx-auto mb-3 sm:mb-4" />
              <h3 className="font-semibold text-white mb-1 sm:mb-2">Fast Delivery</h3>
              <p className="text-xs sm:text-sm text-white/70">3-5 business days</p>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6"
            >
              <Wrench className="w-6 h-6 sm:w-8 sm:h-8 text-gold mx-auto mb-3 sm:mb-4" />
              <h3 className="font-semibold text-white mb-1 sm:mb-2">Expert Install</h3>
              <p className="text-xs sm:text-sm text-white/70">Professional team</p>
            </motion.div>
          </div>
        </div>

        {/* Share Section */}
        <motion.div 
          variants={itemVariants}
          className="bg-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-8 mb-6 sm:mb-8"
        >
          <h3 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center">
            <Share2 className="w-5 h-5 sm:w-6 sm:h-6 mr-3 text-gold" />
            Share Your Design
          </h3>
          
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4">
            {shareOptions.map(({ icon: Icon, label, color }) => (
              <motion.button
                key={label}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleShare(label)}
                className={`${color} text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg flex items-center justify-center gap-2`}
              >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm">{label}</span>
              </motion.button>
            ))}
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleEmailShare}
              className="bg-white/10 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-white/20"
            >
              <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-sm">Email</span>
            </motion.button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-4 sm:mt-6">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleCopyLink}
              className="flex-1 bg-white/10 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg flex items-center justify-center gap-2 relative hover:bg-white/20 transition-colors"
            >
              <Copy className="w-4 h-4 sm:w-5 sm:h-5" />
              {showCopied ? 'Copied!' : 'Copy Link'}
            </motion.button>
            
            <PDFDownloadLink
              document={<OrderPDF orderDetails={{
                customerInfo: formState || {},
                projectDetails: {
                  type: customizationOptions?.type || 'Custom',
                  timeline: formState?.projectTimeline || '',
                  description: formState?.message || ''
                },
                materials: [],
                services: [],
                totals: { subtotal: 0, vat: 0, total: 0 }
              }} />}
              fileName={`order-${new Date().toISOString().split('T')[0]}.pdf`}
              className="flex-1"
            >
              {({ loading }) => (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-gold text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-gold-dark transition-colors"
                  disabled={loading}
                >
                  <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                  {loading ? 'Generating...' : 'Download PDF'}
                </motion.button>
              )}
            </PDFDownloadLink>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => window.print()}
              className="flex-1 bg-white/10 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-white/20 transition-colors"
            >
              <Printer className="w-4 h-4 sm:w-5 sm:h-5" />
              Print Design
            </motion.button>
          </div>
        </motion.div>

        {/* Market Stats */}
        <motion.div 
          variants={itemVariants}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"
        >
          {marketStats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Icon className="w-6 h-6 text-gold" />
                  <span className="text-green-400 text-sm">{stat.trend}</span>
                </div>
                <h4 className="text-2xl font-bold text-white mb-1">{stat.value}</h4>
                <p className="text-white/60 text-sm">{stat.label}</p>
              </div>
            );
          })}
        </motion.div>

        {/* Live Activity Feed */}
        <motion.div 
          variants={itemVariants}
          className="bg-white/5 backdrop-blur-sm rounded-xl p-6 mb-8"
        >
          <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
            <Timer className="w-5 h-5 mr-2 text-gold" />
            Live Activity
          </h3>
          <div className="space-y-4">
            {liveActivity.map((activity, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <div>
                    <p className="text-white">
                      <span className="text-gold font-semibold">{activity.count}</span> people from{' '}
                      <span className="text-gold">{activity.location}</span>
                    </p>
                    <p className="text-white/60 text-sm">
                      {activity.type === 'view' ? 'viewing' : activity.type === 'customize' ? 'customizing' : 'requesting quotes for'}{' '}
                      {activity.product}
                    </p>
                  </div>
                </div>
                <span className="text-white/40 text-sm">{activity.time}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Referral Program */}
        <motion.div 
          variants={itemVariants}
          className="bg-gradient-to-r from-gold/20 to-gold-dark/20 rounded-xl p-4 sm:p-8 text-center"
        >
          <h3 className="text-xl sm:text-2xl font-semibold text-white mb-2 sm:mb-4">
            Love Your Design? Share the Joy!
          </h3>
          <p className="text-sm sm:text-base text-white/70 mb-4 sm:mb-6">
            Refer a friend and both of you get 10% off your next purchase
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            <input
              type="email"
              placeholder="Friend's email address"
              className="flex-1 max-w-sm px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
            />
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-gold text-white px-6 py-2 rounded-lg font-medium hover:bg-gold-dark transition-colors"
            >
              Send Invite
            </motion.button>
          </div>
        </motion.div>

        {/* Contact Information */}
        <motion.div 
          variants={itemVariants}
          className="mt-6 sm:mt-8 text-center"
        >
          <p className="text-white/70 mb-4">
            Questions about your order? Contact our premium support:
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
            <a href="tel:+18001234567" className="text-gold hover:text-gold-dark flex items-center gap-2 transition-colors">
              <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
              +1 (800) 123-4567
            </a>
            <a href="mailto:support@example.com" className="text-gold hover:text-gold-dark flex items-center gap-2 transition-colors">
              <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
              support@example.com
            </a>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};